import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin, Typography, Tag, Button, Popover, List, Space, Divider, Radio } from 'antd';
import { UserOutlined, TeamOutlined, ApartmentOutlined, FolderOutlined, FolderOpenOutlined } from '@ant-design/icons';
import weworkService from '../../services/weworkService';
import api from '../../services/api';

const { Option } = Select;
const { Text } = Typography;

/**
 * 企业微信员工选择器
 * @param {Object} props
 * @param {boolean} props.multiple 是否多选
 * @param {function} props.onChange 选值变化时的回调
 * @param {Array|string|number} props.value 当前选中的值
 * @param {string} props.placeholder 占位文本
 * @param {boolean} props.disabled 是否禁用
 * @param {string} props.width 宽度
 * @param {boolean} props.showGroupSelect 是否显示分组快选功能
 * @param {boolean} props.allowInactive 是否允许选择未激活的员工
 * @param {boolean} props.showDisplayModeSwitch 是否显示展示模式切换功能
 * @param {(emp: any) => boolean} props.filterEmployee 额外的员工过滤函数（返回true才展示）
 * @param {string} props.defaultDisplayMode 默认展示模式 'group' | 'department'
 */
const EmployeeSelect = ({
  multiple = false,
  onChange,
  value,
  placeholder = '请选择员工',
  disabled = false,
  width = '100%',
  showGroupSelect = false, // 默认关闭右侧分组按钮
  allowInactive = false,
  showDisplayModeSwitch = true,
  defaultDisplayMode = 'group',
  filterEmployee,
  ...otherProps
}) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  
  // 获取显示模式：当不展示切换时，强制使用传入的默认模式，忽略本地缓存
  const getStoredDisplayMode = () => {
    if (!showDisplayModeSwitch) {
      return defaultDisplayMode;
    }
    try {
      const stored = localStorage.getItem('employeeSelect_displayMode');
      return stored && (stored === 'group' || stored === 'department') ? stored : defaultDisplayMode;
    } catch (error) {
      console.warn('无法读取localStorage:', error);
      return defaultDisplayMode;
    }
  };

  // 展示模式状态
  const [displayMode, setDisplayMode] = useState(getStoredDisplayMode());  
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  
  // 分组相关状态
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState(new Map()); // groupId -> members
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // 默认折叠

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // 处理显示模式变更并保存到localStorage
  const handleDisplayModeChange = (newMode) => {
    setDisplayMode(newMode);
    try {
      localStorage.setItem('employeeSelect_displayMode', newMode);
      console.log('已保存用户选择的显示模式:', newMode);
    } catch (error) {
      console.warn('无法保存到localStorage:', error);
    }
  };

  // 当不允许切换时，如果外部默认模式变更，强制同步显示模式
  useEffect(() => {
    if (!showDisplayModeSwitch) {
      setDisplayMode(defaultDisplayMode);
    }
  }, [showDisplayModeSwitch, defaultDisplayMode]);
  
  // 规范化 value，确保它们是字符串类型的 ID
  const normalizedValue = useMemo(() => {
    if (value === null || value === undefined) return multiple ? [] : undefined;
    
    if (multiple && Array.isArray(value)) {
      return value.map(id => String(id));
    } else if (!multiple) {
      return value ? String(value) : undefined;
    }
    
    return multiple ? [] : undefined;
  }, [value, multiple]);
  
  // 创建员工ID到员工对象的映射，方便快速查找
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach(emp => {
      map[String(emp.id)] = emp;
    });
    return map;
  }, [employees]);
  
  // 检查是否有选中但找不到对应员工数据的ID
  const missingEmployeeIds = useMemo(() => {
    if (!normalizedValue) return [];
    
    const ids = multiple ? normalizedValue : [normalizedValue];
    return ids.filter(id => id && !employeeMap[id]);
  }, [normalizedValue, employeeMap, multiple]);
  
  // 加载企业微信员工数据
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      setError(null);
      try {
        // 获取所有员工（包括未激活的，用于分组功能兼容）
        const employeeList = await weworkService.getAllEmployees();
        
        // 确保员工ID都是字符串类型
        const normalizedEmployees = employeeList.map(emp => ({
          ...emp,
          id: String(emp.id)
        }));
        
        // 默认不展示未激活的员工（allowInactive=false）
        let finalEmployees = allowInactive 
          ? normalizedEmployees 
          : normalizedEmployees.filter(emp => emp.systemStatus === 1);
        if (typeof filterEmployee === 'function') {
          finalEmployees = finalEmployees.filter(filterEmployee);
        }
        
        setEmployees(finalEmployees);
        console.log('加载员工数据成功，展示', finalEmployees.length, '名员工');
      } catch (error) {
        console.error('加载企业微信员工失败:', error);
        setError('加载员工数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [allowInactive]);

  // 加载部门数据
  useEffect(() => {
    const fetchDepartments = async () => {
      if (displayMode !== 'department') return;
      
      setDepartmentsLoading(true);
      try {
        const response = await api.get('/wework/departments/tree');
        if (response.data) {
          setDepartments(response.data || []);
          console.log('加载部门数据成功，共', response.data.length, '个根部门');
        }
      } catch (error) {
        console.error('加载部门数据失败:', error);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
  }, [displayMode]);

  // 加载分组数据
  useEffect(() => {
    const fetchGroups = async () => {
      if (displayMode !== 'group') return;
      
      setGroupsLoading(true);
      try {
        const response = await api.get('/wework/employee-groups/all');
        if (response.data.success) {
          const groupsList = response.data.data || [];
          setGroups(groupsList);
          console.log('加载分组数据成功，共', groupsList.length, '个分组');
          
          // 为每个分组加载成员数据
          const membersMap = new Map();
          for (const group of groupsList) {
            try {
              const membersResponse = await api.get(`/wework/employee-groups/${group.id}/members`, {
                params: { page: 0, size: 1000 }
              });
              if (membersResponse.data.success) {
                membersMap.set(group.id, membersResponse.data.content || []);
              }
            } catch (error) {
              console.error(`加载分组${group.name}的成员失败:`, error);
              membersMap.set(group.id, []);
            }
          }
          setGroupMembers(membersMap);
          console.log('加载所有分组成员数据完成');
        }
      } catch (error) {
        console.error('加载分组数据失败:', error);
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [displayMode]);

  // 当加载完员工数据后，如果发现有缺失的员工ID，尝试加载这些特定员工的信息
  useEffect(() => {
    const loadMissingEmployees = async () => {
      if (missingEmployeeIds.length === 0 || loading) return;
      
      console.log('尝试加载缺失的员工信息:', missingEmployeeIds);
      
      // 这里可以添加代码来加载特定ID的员工信息
      // 如果API支持通过ID列表查询员工信息，可以在这里调用
      // 例如：
      // try {
      //   const missingEmployees = await weworkService.getEmployeesByIds(missingEmployeeIds);
      //   if (missingEmployees && missingEmployees.length > 0) {
      //     setEmployees(prev => {
      //       const newEmployees = [...prev];
      //       missingEmployees.forEach(emp => {
      //         if (!newEmployees.some(e => String(e.id) === String(emp.id))) {
      //           newEmployees.push({...emp, id: String(emp.id)});
      //         }
      //       });
      //       return newEmployees;
      //     });
      //   }
      // } catch (error) {
      //   console.error('加载特定员工信息失败:', error);
      // }
    };
    
    loadMissingEmployees();
  }, [missingEmployeeIds, loading]);

  // 处理选择变更
  const handleChange = async (newValue) => {
    // 检查是否选择了分组或部门
    const latestValue = Array.isArray(newValue) ? newValue[newValue.length - 1] : newValue;
    
    if (latestValue && typeof latestValue === 'string') {
      // 处理分组选择
      if (latestValue.startsWith('group-')) {
        const groupId = latestValue.replace('group-', '');
        await handleGroupSelect({ id: parseInt(groupId) });
        return;
      }
      
      // 处理部门选择
      if (latestValue.startsWith('dept-')) {
        const deptId = latestValue.replace('dept-', '');
        await handleDepartmentSelect(parseInt(deptId));
        return;
      }
    }
    
    // 正常的员工选择
    if (onChange) {
      onChange(newValue);
    }
  };
  
  // 处理部门选择 - 选择部门下所有成员
  const handleDepartmentSelect = async (departmentId) => {
    try {
      const response = await api.get(`/wework/employees/by-department/${departmentId}`);
      
      if (response.data.success) {
        const departmentMembers = response.data.data || [];
        
        // 根据 allowInactive 配置过滤成员
        const validMembers = allowInactive 
          ? departmentMembers 
          : departmentMembers.filter(member => member.systemStatus === 1);
        
        const memberIds = validMembers.map(member => String(member.id));
        
        if (multiple) {
          // 多选模式：将部门成员添加到现有选择中
          const currentIds = normalizedValue || [];
          const newIds = [...new Set([...currentIds, ...memberIds])]; // 去重
          if (onChange) {
            onChange(newIds);
          }
        } else {
          // 单选模式：选择部门中的第一个激活成员
          if (memberIds.length > 0) {
            if (onChange) {
              onChange(memberIds[0]);
            }
          }
        }
      }
    } catch (error) {
      console.error('加载部门成员失败:', error);
    }
  };

  // 处理分组选择
  const handleGroupSelect = async (group) => {
    try {
      // 从预加载的数据中获取分组成员
      const members = groupMembers.get(group.id) || [];
      
      // 根据 allowInactive 配置过滤成员
      const validMembers = allowInactive 
        ? members 
        : members.filter(member => member.systemStatus === 1);
      
      const memberIds = validMembers.map(member => String(member.id));
      
      if (multiple) {
        // 多选模式：将分组成员添加到现有选择中
        const currentIds = normalizedValue || [];
        const newIds = [...new Set([...currentIds, ...memberIds])]; // 去重
        if (onChange) {
          onChange(newIds);
        }
      } else {
        // 单选模式：选择分组中的第一个激活成员
        if (memberIds.length > 0) {
          if (onChange) {
            onChange(memberIds[0]);
          }
        }
      }
    } catch (error) {
      console.error('处理分组选择失败:', error);
    }
  };


  // 计算选择数量
  const selectedCount = useMemo(() => {
    if (!multiple || !normalizedValue) return 0;
    return normalizedValue.length;
  }, [normalizedValue, multiple]);


  // 构建部门员工选项
  const buildDepartmentOptions = useMemo(() => {
    if (displayMode !== 'department' || departments.length === 0) return [];
    
    const options = [];
    
    const buildDepartmentTree = (depts, level = 0) => {
      depts.forEach(dept => {
        // 添加部门标题（可选择，选择后会选中该部门所有成员）
        options.push(
          <Option key={`dept-${dept.id}`} value={`dept-${dept.id}`} label={dept.name} style={{ 
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              paddingLeft: level * 16
            }}>
              <FolderOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <Text strong>{dept.name}</Text>
            </div>
          </Option>
        );
        
        // 添加该部门的员工
        const departmentEmployees = employees.filter(emp => {
          try {
            if (emp.departments && emp.departments.length > 0) {
              const deptIds = Array.isArray(emp.departments) 
                ? emp.departments 
                : JSON.parse(emp.departments);
              return deptIds.includes(dept.id);
            }
          } catch (e) {
            console.warn('解析员工部门信息失败:', e);
          }
          return false;
        })
        // 仅展示激活成员（当不允许未激活时）
        .filter(emp => (allowInactive ? true : emp.systemStatus === 1))
        // 额外过滤
        .filter(emp => (typeof filterEmployee === 'function' ? filterEmployee(emp) : true));
        
        departmentEmployees.forEach(employee => {
          const isActive = employee.systemStatus === 1;
          const isWeworkActive = employee.status === 1;
          
          options.push(
            <Option 
              key={`dept-${dept.id}-emp-${employee.id}`} 
              value={String(employee.id)}  // 确保value是字符串类型
              title={`${employee.name} - ${dept.name}`}
              disabled={!allowInactive && !isActive}
              label={employee.name}  // 用于单选模式的干净显示
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                paddingLeft: (level + 1) * 16,
                opacity: isActive ? 1 : 0.6
              }}>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text style={{ color: isActive ? undefined : '#8c8c8c' }}>
                  {employee.name}
                </Text>
                
                {/* 状态标签 */}
                {!isActive && (
                  <Tag color="red" size="small" style={{ marginLeft: 8 }}>
                    系统未激活
                  </Tag>
                )}
                {isActive && !isWeworkActive && (
                  <Tag color="orange" size="small" style={{ marginLeft: 8 }}>
                    企微未激活
                  </Tag>
                )}
              </div>
            </Option>
          );
        });
        
        // 递归处理子部门
        if (dept.children && dept.children.length > 0) {
          buildDepartmentTree(dept.children, level + 1);
        }
      });
    };
    
    buildDepartmentTree(departments);
    return options;
  }, [departments, employees, displayMode, allowInactive]);

  // 创建员工选项的通用函数
  const createEmployeeOption = (employee, prefix = '', groupName = '', showIndent = false) => {
    const isActive = employee.systemStatus === 1;
    const isWeworkActive = employee.status === 1;
    
    return (
      <Option 
        key={`${prefix}${employee.id}`} 
        value={String(employee.id)}  // 确保value是字符串类型
        title={`${employee.name}${groupName ? ` - ${groupName}` : (employee.department ? ` - ${Array.isArray(employee.department) ? employee.department[0] : employee.department}` : '')}`}
        disabled={!allowInactive && !isActive}
        label={employee.name}  // 用于单选模式的干净显示
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          paddingLeft: showIndent ? 16 : 0, // 根据参数决定是否缩进
          opacity: isActive ? 1 : 0.6
        }}>
          <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <Text style={{ color: isActive ? undefined : '#8c8c8c' }}>
            {employee.name}
          </Text>
          
          {/* 状态标签 */}
          {!isActive && (
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>
              系统未激活
            </Tag>
          )}
          {isActive && !isWeworkActive && (
            <Tag color="orange" size="small" style={{ marginLeft: 8 }}>
              企微未激活
            </Tag>
          )}
        </div>
      </Option>
    );
  };

  // 获取所有可能被选中的员工选项（包括来自分组和部门的员工）
  const getAllEmployeeOptions = useMemo(() => {
    const allOptions = [];
    const addedEmployeeIds = new Set();
    
    // 如果有搜索内容，使用去重的搜索结果
    if (searchValue && searchValue.trim()) {
      const searchTerm = searchValue.toLowerCase();
      const searchedEmployees = new Map(); // 用于去重：employeeId -> employee
      
      // 从所有员工中搜索匹配的
      employees
        // 仅展示激活成员（当不允许未激活时，加载阶段已过滤，这里再次确保）
        .filter(emp => allowInactive ? true : emp.systemStatus === 1)
        .forEach(emp => {
        const empName = emp.name ? emp.name.toLowerCase() : '';
        const empId = String(emp.id);
        
        if (empName.includes(searchTerm) || empId.includes(searchTerm)) {
          if (!searchedEmployees.has(empId)) {
            searchedEmployees.set(empId, emp);
          }
        }
      });
      
      // 将搜索到的员工转为选项
      searchedEmployees.forEach((employee, empId) => {
        allOptions.push(createEmployeeOption(employee, 'search-', '', false)); // 搜索结果不缩进
        addedEmployeeIds.add(empId);
      });
    } else {
      // 没有搜索内容时，使用原来的分组/部门逻辑
      if (displayMode === 'group') {
        // 分组模式：添加分组标题和成员
        groups.forEach(group => {
          const members = groupMembers.get(group.id) || [];
          const isExpanded = expandedGroups.has(group.id);
          
          // 添加分组标题
          allOptions.push(
            <Option key={`group-${group.id}`} value={`group-${group.id}`} label={group.name} style={{ 
              fontWeight: 'bold'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div 
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: group.color || '#1890ff',
                      borderRadius: '50%',
                      marginRight: 8
                    }}
                  />
                  <Text strong>{group.name}</Text>
                  {/* <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    ({members.length}人)
                  </Text> */}
                </div>
                <Button 
                  type="link" 
                  size="small" 
                  onMouseDown={(e) => e.preventDefault()} 
                  onClick={(e) => { e.stopPropagation(); toggleGroupExpanded(group.id); }}
                >
                  {isExpanded ? '收起' : '展开'}
                </Button>
              </div>
            </Option>
          );
          
          // 添加分组成员（折叠时不展示；仅展示激活成员，允许在多个分组中重复显示）
          if (isExpanded) {
            let displayedMembers = allowInactive ? members : members.filter(m => m.systemStatus === 1);
            if (typeof filterEmployee === 'function') {
              displayedMembers = displayedMembers.filter(filterEmployee);
            }
            displayedMembers.forEach((member) => {
              const memberId = String(member.id); // 统一使用字符串类型
              // 使用稳定的key：group-id + member-id 的组合，避免使用不稳定的index
              allOptions.push(createEmployeeOption(member, `group-${group.id}-member-${memberId}-`, group.name, true)); // 分组成员需要缩进
              addedEmployeeIds.add(memberId); // 仍然记录，用于后续的选中项处理
            });
          }
        });
      } else if (displayMode === 'department') {
        // 部门模式：使用现有的buildDepartmentOptions
        allOptions.push(...buildDepartmentOptions);
        
        // 在部门模式下，员工可以在多个部门中重复显示，所以不需要记录已添加的员工ID
        // 只需要确保部门选项本身不被重复添加到后面的逻辑中
      }
    }
    
    // 不再额外追加“仅选中但未在列表中显示”的员工项，避免分组折叠时重复展示
    
    return allOptions;
  }, [displayMode, groups, groupMembers, buildDepartmentOptions, employees, normalizedValue, allowInactive, searchValue, expandedGroups]);

  // 渲染模式切换内容
  const renderModeSwitch = () => {
    if (!showDisplayModeSwitch) return null;
    
    return (
      <div style={{ marginBottom: 8 }}>
        <Radio.Group 
          value={displayMode} 
          onChange={(e) => handleDisplayModeChange(e.target.value)}
          size="small"
        >
          <Radio.Button value="group">
            <TeamOutlined style={{ marginRight: 4 }} />
            分组
          </Radio.Button>
          <Radio.Button value="department">
            <ApartmentOutlined style={{ marginRight: 4 }} />
            通讯录
          </Radio.Button>
        </Radio.Group>
      </div>
    );
  };

  // 自定义标签渲染函数，去除缩进
  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const employee = employees.find(emp => String(emp.id) === String(value));
    
    if (employee) {
      return (
        <Tag
          color="default"
          closable={closable}
          onClose={onClose}
          style={{ marginRight: 3, fontSize: 14}}
        >
          {employee.name}
        </Tag>
      );
    }
    
    return (
      <Tag
        color="default"
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };

  // 获取选中员工的显示名称（用于单选模式）
  const getSelectedEmployeeName = () => {
    if (!normalizedValue || multiple) return undefined;
    const employee = employees.find(emp => String(emp.id) === String(normalizedValue));
    return employee ? employee.name : normalizedValue;
  };

  // 单选模式下，使用 labelInValue 以确保在选项尚未加载时也能显示姓名
  const singleSelectValue = useMemo(() => {
    if (multiple) return undefined;
    if (!normalizedValue) return undefined;
    const label = getSelectedEmployeeName();
    return { value: normalizedValue, label };
  }, [multiple, normalizedValue, employees]);

  return (
    <>
      <div style={{ position: 'relative', width }}>
        {renderModeSwitch()}
        
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            mode={multiple ? 'multiple' : undefined}
            showSearch
            value={multiple ? normalizedValue : singleSelectValue}
            placeholder={placeholder}
            style={{ flex: 1 }}
            labelInValue={!multiple}
            onChange={(val) => {
              if (multiple) {
                handleChange(val);
              } else {
                const extracted = val && typeof val === 'object' && 'value' in val ? val.value : val;
                handleChange(extracted);
              }
            }}
            onSearch={setSearchValue}
            allowClear
            onClear={() => onChange && onChange(multiple ? [] : undefined)}
            loading={loading || (displayMode === 'department' && departmentsLoading) || (displayMode === 'group' && groupsLoading)}
            disabled={disabled}
            filterOption={false}  // 禁用默认过滤，使用自定义搜索逻辑
            notFoundContent={loading || departmentsLoading || groupsLoading ? <Spin size="small" /> : error || '暂无数据'}
            tagRender={multiple ? tagRender : undefined}  // 多选模式使用自定义标签渲染
            optionLabelProp={multiple ? undefined : "label"}  // 单选模式使用label属性显示
            {...otherProps}
          >
            {getAllEmployeeOptions}
          </Select>
        </div>
        
        {/* 选择计数标签 */}
        {multiple && selectedCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '50%',
            right: '32px',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 1
          }}>
            <Tag color="blue" size="small">
              已选择 {selectedCount} 人
            </Tag>
          </div>
        )}
      </div>
      
      {missingEmployeeIds.length > 0 && !loading && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#ff4d4f' }}>
          警告：有{missingEmployeeIds.length}个员工ID未找到对应信息
        </div>
      )}
    </>
  );
};

export default EmployeeSelect; 